/**
 * Font registry for the Text behavior.
 *
 * Maps spec `(fontFamily, fontWeight, fontStyle)` triples to bundled MSDF
 * font assets (BMFont JSON definition + MSDF PNG atlas). This is the single
 * point of indirection between the engine-agnostic spec values and the
 * Babylon-specific asset URLs. Other engines (Unity, native) will have
 * their own equivalent.
 *
 * ## Asset distribution
 *
 * Two sources, in this order of authority:
 *
 * 1. **Bundled.** Atlases vendored inside this package at build time, picked
 *    up by the glob below. Babylon's own asset CDN is documented as
 *    non-production and unversioned (Babylon docs, Setup → Framework
 *    packages → CDN), so we never fetch faces from a shared CDN. For these,
 *    the package version *is* the asset version.
 * 2. **Hosted.** Faces registered at runtime from Library Admin — see
 *    `hostedFonts.ts`, which owns fetching and is the only caller of
 *    {@link registerFontAssetUrls}. For these the URL is the version: a pack
 *    pins immutable per-upload URLs and carries its own revision, because a
 *    package version can no longer imply a byte-identical render.
 *
 * Bundled wins on a key collision, always. A hosted pack can only ADD faces;
 * it can never shadow one that shipped, so no deployment can change how an
 * existing scene renders by uploading something.
 *
 * ## Filename convention
 *
 * Each face in `src/assets/fonts/` follows the pattern
 * `<family>-<weight>[-italic].{json,png}`, lowercased:
 *
 * - `roboto-regular.json` / `.png`
 * - `roboto-bold.json` / `.png`
 * - `roboto-italic.json` / `.png`         (regular weight implied)
 * - `roboto-bold-italic.json` / `.png`
 * - `inter-*` (same shape)
 *
 * Adding a new face is a two-file drop into `src/assets/fonts/`; the
 * registry picks it up via Vite's glob import. No code changes needed.
 *
 * ## Fallback chain
 *
 * Resolution degrades gracefully along three axes (CSS-style), so partial
 * registries still produce a usable result:
 *
 * 1. Exact match `(family, weight, style)` — best.
 * 2. Drop `style` — `(family, weight, normal)`.
 * 3. Drop `weight` — `(family, regular, normal)`.
 * 4. Drop `family`, KEEP weight and style — `(Roboto, weight, style)`.
 * 5. Then drop `style` — `(Roboto, weight, normal)`.
 * 6. Then drop `weight` — `(Roboto, regular, normal)`, the default.
 *
 * Steps 4-5 are what the Text spec requires: "Unknown values fall back to the
 * default family **at the requested weight/style**" (`textdata.ts`,
 * conformance requirement 2). The family is the axis that failed; the weight
 * and style the author asked for are still expressible, because Roboto ships
 * bold, italic and bold-italic. Dropping them as well turned a request for
 * "SomeUnknownFace bold" into Roboto Regular, which reads as a different
 * sentence rather than a different typeface.
 *
 * Steps 2-3 still come first, so a family that IS available keeps the
 * paragraph inside it: `Oswald italic` resolves to Oswald Regular, never to
 * Roboto Italic.
 *
 * The first hit wins; a one-shot warning logs the substitution.
 */
import { Logger } from '../../utils/Logger';
import { DEFAULT_FAMILY, DEFAULT_STYLE, DEFAULT_WEIGHT, keyOf, resolveAgainst, type FontKey } from './fontResolve';
import type {
    BundledFontFamily,
    FontFamily,
    FontWeight,
    FontStyle,
    FontVariant,
} from '../../scenedata/behaviors/textdata';

export { resolveAgainst, type FontKey };

/**
 * Map between the spec's display family name and its filename slug. The
 * filename slug is kebab-case, lowercase, ASCII-only — chosen for sensible
 * cross-platform filesystem behaviour. The display name is what authors
 * see in the spec and write in `fontFamily`.
 *
 * Adding a BUNDLED family: add it to the `BundledFontFamily` union and to
 * this map; drop the atlas files into `src/assets/fonts/` using the slug as
 * the filename prefix (`<slug>-<weight>[-italic].{json,png}`). A hosted
 * family needs none of this — it has no slug because it has no filename.
 */
const FAMILY_SLUGS: Record<BundledFontFamily, string> = {
    'Roboto': 'roboto',
    'Inter': 'inter',
    'Source Serif 4': 'source-serif-4',
    'JetBrains Mono': 'jetbrains-mono',
    'Oswald': 'oswald',
    'Bungee': 'bungee',
    'Caveat': 'caveat',
};
const SLUG_TO_FAMILY: Record<string, BundledFontFamily> = Object.fromEntries(
    Object.entries(FAMILY_SLUGS).map(([family, slug]) => [slug, family as BundledFontFamily]),
);

/**
 * Recognised weight/style suffix tokens. Order matters: longer suffixes
 * are tried first so `bold-italic` matches before `italic` or `bold`.
 */
const SUFFIXES: ReadonlyArray<readonly [string, FontWeight, FontStyle]> = [
    ['-bold-italic', 'bold', 'italic'],
    ['-bold', 'bold', 'normal'],
    ['-italic', 'regular', 'italic'],
    ['-regular', 'regular', 'normal'],
];

export interface FontAssetUrls {
    definitionUrl: string;
    textureUrl: string;
    /** Glyph outlines for extruded text (`tools/build-font-outlines.ts`); bundled faces only. */
    outlineUrl?: string;
}

/**
 * Are both halves of a face's address there? A pack manifest is JSON from
 * another machine, so a face can reach us without one, or with something that
 * is not a string.
 */
export function hasFontAssetUrls(value: unknown): value is FontAssetUrls {
    const urls = value as Partial<FontAssetUrls> | null | undefined;
    return typeof urls?.definitionUrl === 'string' && urls.definitionUrl.length > 0
        && typeof urls.textureUrl === 'string' && urls.textureUrl.length > 0
        && (urls.outlineUrl === undefined || typeof urls.outlineUrl === 'string');
}

// Vite turns each matched file into the URL of an emitted asset at build time.
// Eager import keeps the registry synchronous; the URLs are tiny strings, not the
// asset bytes, so eager loading is cheap. The `as unknown as ...` cast is
// because Vite's glob types return `unknown` when both `query` and `import`
// are set; we know each value is the URL string.
//
// `no-inline` is required because Vite library mode (`build.lib`) otherwise
// force-inlines all assets as base64 data URIs regardless of `assetsInlineLimit`.
// With `no-inline`, fonts are emitted as separate files in `dist/`, named in
// vite.config.js, and the chunk imports their URLs: atlases (~6.7 MB total)
// stay out of the JS.
const jsonUrls = import.meta.glob(
    '../../assets/fonts/*.json',
    { eager: true, query: '?url&no-inline', import: 'default' },
) as unknown as Record<string, string>;
const pngUrls = import.meta.glob(
    '../../assets/fonts/*.png',
    { eager: true, query: '?url&no-inline', import: 'default' },
) as unknown as Record<string, string>;
const outlineUrls = import.meta.glob(
    '../../assets/fonts/outlines/*.json',
    { eager: true, query: '?url&no-inline', import: 'default' },
) as unknown as Record<string, string>;

// Everything the glob found. Frozen conceptually: `bundledFonts()` answers
// from this, and it is what `registerFontAssetUrls` refuses to overwrite.
const BUNDLED: ReadonlyMap<string, FontAssetUrls> = buildRegistry();

// The live view resolution walks: bundled, plus whatever `hostedFonts.ts` has
// registered. Seeded from BUNDLED, so with nothing registered this map is
// BUNDLED entry-for-entry and every existing scene resolves exactly as it did
// before hosted faces existed.
const REGISTRY: Map<string, FontAssetUrls> = new Map(BUNDLED);

// The one-shot substitution gate: family -> the requested keys already
// reported. Per family so that a face arriving for one family re-arms only that
// family's reports; a page publishing twenty families would otherwise get the
// whole gate reset by each of the twenty arrivals.
const warnedKeys = new Map<string, Set<string>>();

/**
 * Resolve a font triple to asset URLs. Walks the fallback chain
 * (style → weight → family, then the default family at the requested
 * weight/style) until it finds a match. The default
 * font (Roboto Regular Normal) MUST be bundled for the registry to be
 * usable — `buildRegistry` throws on startup if it is missing.
 */
export function resolveFontAssets(
    family: FontFamily,
    weight: FontWeight,
    style: FontStyle,
): FontAssetUrls {
    return resolveAgainst(REGISTRY, { family, weight, style }, warnOnce);
}


/**
 * Build a registry key. Exposed for tests that construct synthetic
 * registries to exercise the fallback logic.
 */
export function fontRegistryKey(k: FontKey): string {
    return keyOf(k);
}

/**
 * List every (family, weight, style) actually bundled in this build.
 * Used by tests and diagnostics; for editor pickers, prefer
 * `listFontVariants(family)`.
 */
export function bundledFonts(): FontKey[] {
    return Array.from(BUNDLED.keys()).map(parseKey);
}

/**
 * The families this package vendors atlases for, from the slug map rather than
 * from the glob.
 *
 * The distinction matters for exactly one consumer: the sandbox bundles its
 * converter with esbuild, where the registry's eager `import.meta.glob` of the
 * atlases is shimmed away and {@link bundledFonts} therefore comes back empty.
 * `FAMILY_SLUGS` is a plain object literal and survives that, so this is the
 * inventory a build tool can trust — and it is what lets the sandbox derive
 * its renderable-family list instead of hand-maintaining a copy of this list
 * that drifts every time a family lands.
 */
export function bundledFontFamilies(): BundledFontFamily[] {
    return Object.keys(FAMILY_SLUGS) as BundledFontFamily[];
}

/**
 * Register a runtime-loaded face's asset URLs. The ONLY writer of the hosted
 * half of the registry; `hostedFonts.ts` is its only caller, and it installs
 * the matching measurement metrics in the same breath (a face whose URLs
 * resolve but whose metrics don't would measure as Roboto and render as
 * itself — the one failure mode worse than a visible substitution).
 *
 * Returns false, having changed nothing, when the key is bundled (a hosted
 * pack adds faces, it never shadows a shipped one) or when either URL is not a
 * non-empty string: a face with half its address cannot be drawn, and would be
 * fetched as the literal text "undefined".
 */
export function registerFontAssetUrls(key: FontKey, urls: FontAssetUrls): boolean {
    const k = keyOf(key);
    if (BUNDLED.has(k)) return false;
    if (!hasFontAssetUrls(urls)) return false;
    REGISTRY.set(k, urls);
    addVariant(key);
    // A family that previously fell back has a live one-shot warning recorded
    // against it. Clearing lets a LATER failure of the same family be reported
    // rather than swallowed as "already warned". Only this family's: what other
    // families have already reported is still true.
    warnedKeys.delete(key.family);
    return true;
}

/**
 * Announce a face the deployment PUBLISHES but has not fetched yet.
 *
 * The gap this closes: fetching a definition costs 50-870 KB, and answering
 * "can you draw this?" for every published family would mean fetching all
 * of them at boot to answer a question most pages never ask. But the answer has
 * to be synchronous — the sandbox converter asks it BEFORE it emits a family
 * name, and an answer that arrived after the scene was written is no answer.
 *
 * So a declaration is the catalogue entry, not the face: it makes
 * {@link listFontVariants} and {@link isFamilyAvailable} say yes, and the
 * bytes are fetched only when something actually asks to draw the family
 * ({@link import('./hostedFonts').ensureFontFamily}). Between those two moments
 * the paragraph draws through the fallback chain and re-measures when the face
 * lands — the late-arrival path `hostedFonts.ts` documents, which is why "yes"
 * is honest here rather than optimistic.
 *
 * Returns false, having changed nothing, for a bundled key: those are already
 * listed, and by something stronger than a promise.
 */
export function declareFontVariant(key: FontKey): boolean {
    const k = keyOf(key);
    if (BUNDLED.has(k)) return false;
    // A face newly promised changes what a request for it means: "unavailable"
    // (reported while it was undeclared) becomes "loading". Re-arm the family's
    // reports so that change is reported rather than swallowed by the one-shot
    // gate. Re-declaring what is already declared changes nothing, so it does
    // not — a host may declare its whole catalogue again on every boot.
    if (!declaredKeys.has(k)) warnedKeys.delete(key.family);
    declaredKeys.add(k);
    addVariant(key);
    return true;
}

/**
 * Withdraw every face of a family that was declared — the inverse of
 * {@link declareFontVariant}, for a family the deployment publishes but that
 * will never load (a permanent 404, a catalogue entry retired since boot).
 *
 * Without it a declaration outlives the failure it made a promise about:
 * {@link isFamilyAvailable} keeps saying yes, and an authoring tool keeps
 * emitting a name nothing can draw — the silent substitution, one step
 * earlier.
 *
 * A face whose bytes DID arrive is registered, not merely declared, and stays:
 * it is real, and withdrawing a promise must not un-draw what is already
 * drawn. Returns whether any declaration was withdrawn.
 */
export function undeclareFamilyVariants(family: string): boolean {
    let withdrawn = false;
    for (const k of [...declaredKeys]) {
        const key = parseKey(k);
        if (key.family !== family) continue;
        declaredKeys.delete(k);
        if (!REGISTRY.has(k)) removeVariant(key);
        withdrawn = true;
    }
    // What the family reported while its faces were promised is stale: a
    // request for it is now "unavailable", not "loading", and should say so.
    warnedKeys.delete(family);
    return withdrawn;
}

/**
 * Is this face published-but-not-yet-loaded? Only the fallback warning reads
 * it, to tell "still loading" apart from "we have never heard of this".
 */
export function isVariantDeclared(key: FontKey): boolean {
    return declaredKeys.has(keyOf(key));
}

const declaredKeys = new Set<string>();

/**
 * Does this family ship inside the package? Packaging's question, and the
 * orthogonal one to {@link isFamilyAvailable}: a family can be renderable
 * without shipping (hosted) and — once families move out — the answer here is
 * what decides whether a deployment has to have registered anything at all.
 */
export function isFamilyBundled(family: string): boolean {
    for (const key of BUNDLED.keys()) {
        if (parseKey(key).family === family) return true;
    }
    return false;
}

/**
 * Does this exact face ship inside the package?
 *
 * The per-face version of {@link isFamilyBundled}, and the reason a hosted
 * load can report WHY a face was not installed: a bundled key is refused by
 * design ({@link registerFontAssetUrls}), which is a different outcome from a
 * fetch that failed, and a host cannot act on the two the same way.
 */
export function isVariantBundled(key: FontKey): boolean {
    return BUNDLED.has(keyOf(key));
}

/**
 * Will this family be drawn — bundled, registered by a hosted pack, or
 * declared by one this deployment publishes? The gate an authoring tool asks
 * BEFORE emitting a family name, and the thing that keeps an open `fontFamily`
 * from re-introducing silent substitution: a name that fails this is a scene
 * declaring a dependency it did not bring, not a font we happen not to have.
 *
 * "Will be", not "can right now", is deliberate. A declared face is fetched
 * when something asks to draw it and the paragraph re-measures when it lands
 * ({@link declareFontVariant}); answering no for it would make the converter
 * discard a family the engine is about to have, which is the substitution this
 * exists to prevent, one step earlier.
 *
 * Same answer as `listFontVariants(family).length > 0`, which is what
 * `engineRendersFamily` in the sandbox's converter already reads; this is the
 * named version of that question.
 */
export function isFamilyAvailable(family: string): boolean {
    return listFontVariants(family).length > 0;
}

// Faces grouped by family. Seeded from the bundled set at module load and
// extended by `registerFontAssetUrls`. Variants within each family are sorted
// deterministically (regular before bold, normal before italic) so editor
// pickers can rely on a stable order. Values are handed out as
// ReadonlyArray<...> so honest TypeScript callers can't corrupt the cache by
// sorting/pushing in place.
const VARIANTS_BY_FAMILY: Map<FontFamily, FontVariant[]> = (() => {
    const m = new Map<FontFamily, FontVariant[]>();
    for (const key of REGISTRY.keys()) {
        const { family, weight, style } = parseKey(key);
        const list = m.get(family) ?? [];
        list.push({ weight, style });
        m.set(family, list);
    }
    for (const list of m.values()) list.sort(compareVariants);
    return m;
})();

function addVariant(key: FontKey): void {
    const list = VARIANTS_BY_FAMILY.get(key.family) ?? [];
    if (list.some(v => v.weight === key.weight && v.style === key.style)) return;
    list.push({ weight: key.weight, style: key.style });
    list.sort(compareVariants);
    VARIANTS_BY_FAMILY.set(key.family, list);
}

/**
 * In place, like {@link addVariant}: a list a caller is holding stays current,
 * and an emptied one stays in the map so a later declaration extends the same
 * array rather than orphaning the held one.
 */
function removeVariant(key: FontKey): void {
    const list = VARIANTS_BY_FAMILY.get(key.family);
    if (!list) return;
    const at = list.findIndex(v => v.weight === key.weight && v.style === key.style);
    if (at >= 0) list.splice(at, 1);
}

const EMPTY_VARIANTS: ReadonlyArray<FontVariant> = [];

function compareVariants(a: FontVariant, b: FontVariant): number {
    // regular < bold; normal < italic.
    const weightOrder = (w: FontWeight) => w === 'regular' ? 0 : 1;
    const styleOrder = (s: FontStyle) => s === 'normal' ? 0 : 1;
    const dw = weightOrder(a.weight) - weightOrder(b.weight);
    return dw !== 0 ? dw : styleOrder(a.style) - styleOrder(b.style);
}

/**
 * List the (weight, style) variants this build will draw for a given family —
 * bundled faces, faces a hosted pack has registered, and faces this deployment
 * has declared and will fetch on demand — sorted (regular before bold, normal
 * before italic). Useful for editors that want to disable/grey-out unsupported
 * toggles (e.g. Oswald has no italic, Bungee is single-weight).
 *
 * The BUNDLED set of families comes from the spec — read it via
 * `getBehaviorSchema("Text")`'s `fontFamily` enum options. Since that field
 * is an OPEN enum (`openEnum: true`), the schema's option list is the
 * guaranteed families, not the complete one; this function is the complete
 * one, live, and is therefore also the honest answer to "can the engine draw
 * this family at all?" (`length > 0`, which is what `isFamilyAvailable` and
 * the sandbox converter's `engineRendersFamily` both read).
 *
 * Returns an empty array for a family with no faces — which is now a
 * meaningful answer rather than a defensive one: it is exactly the case an
 * authoring tool must refuse to emit.
 *
 * Returned array is shared across calls; do not mutate it. It is extended in
 * place when a hosted pack registers another cut of the same family, so a
 * held reference stays current rather than going stale.
 */
export function listFontVariants(family: FontFamily): ReadonlyArray<FontVariant> {
    return VARIANTS_BY_FAMILY.get(family) ?? EMPTY_VARIANTS;
}

function buildRegistry(): Map<string, FontAssetUrls> {
    const byBasename = new Map<string, Partial<FontAssetUrls>>();

    for (const [path, url] of Object.entries(jsonUrls)) {
        const basename = basenameOf(path);
        const entry = byBasename.get(basename) ?? {};
        entry.definitionUrl = url;
        byBasename.set(basename, entry);
    }
    for (const [path, url] of Object.entries(pngUrls)) {
        const basename = basenameOf(path);
        const entry = byBasename.get(basename) ?? {};
        entry.textureUrl = url;
        byBasename.set(basename, entry);
    }

    const registry = new Map<string, FontAssetUrls>();
    for (const [basename, urls] of byBasename) {
        if (!urls.definitionUrl || !urls.textureUrl) {
            Logger.warn(`[Text] Font "${basename}" is missing a JSON or PNG file; skipping.`);
            continue;
        }
        const key = parseFilenameToKey(basename);
        if (!key) {
            // Filename doesn't match our naming convention — ignore (could be
            // an unrelated asset that happened to be in src/assets/fonts/).
            continue;
        }
        const outlineUrl = Object.entries(outlineUrls).find(([path]) => basenameOf(path) === basename)?.[1];
        registry.set(keyOf(key), { definitionUrl: urls.definitionUrl, textureUrl: urls.textureUrl, outlineUrl });
    }

    const defaultKey = keyOf({ family: DEFAULT_FAMILY, weight: DEFAULT_WEIGHT, style: DEFAULT_STYLE });
    if (!registry.has(defaultKey)) {
        throw new Error(
            `[Text] Default font "${DEFAULT_FAMILY} ${DEFAULT_WEIGHT} ${DEFAULT_STYLE}" is required but not bundled. ` +
            `Expected file: src/assets/fonts/roboto-regular.{json,png}`,
        );
    }
    return registry;
}

function basenameOf(path: string): string {
    // path is like '../../assets/fonts/roboto-regular.json' — strip dir + ext.
    const lastSlash = path.lastIndexOf('/');
    const lastDot = path.lastIndexOf('.');
    return path.slice(lastSlash + 1, lastDot);
}

/**
 * Parse a filename like `source-serif-4-bold-italic` into a `FontKey`.
 * Strategy: match the longest known weight/style suffix and treat
 * everything before it as the family slug. Returns `null` for filenames
 * that don't match (so unrelated files in the directory are silently
 * ignored) or for filenames whose family slug isn't in the registry.
 */
function parseFilenameToKey(basename: string): FontKey | null {
    for (const [suffix, weight, style] of SUFFIXES) {
        if (!basename.endsWith(suffix)) continue;
        const slug = basename.slice(0, -suffix.length);
        const family = SLUG_TO_FAMILY[slug];
        if (!family) return null;
        return { family, weight, style };
    }
    return null;
}

/**
 * Inverse of {@link keyOf}. Weight and style are closed sets with no `|` in
 * them, so the last two separators are always theirs and everything before
 * them is the family — whatever characters a hosted family's name carries. A
 * plain `split('|')` cuts a name like "Pipe|Sans" in the wrong place.
 */
function parseKey(key: string): FontKey {
    const styleAt = key.lastIndexOf('|');
    const weightAt = key.lastIndexOf('|', styleAt - 1);
    return {
        family: key.slice(0, weightAt),
        weight: key.slice(weightAt + 1, styleAt) as FontWeight,
        style: key.slice(styleAt + 1) as FontStyle,
    };
}

/** Why a face other than the requested one was drawn. */
export type FontSubstitutionReason =
    /** The family is published by this deployment; its atlas has not arrived yet. */
    | 'loading'
    /** Nothing bundled or declared carries this family. */
    | 'unavailable';

/** One substitution: what was asked for, what was drawn, and why. */
export interface FontSubstitution {
    requested: FontKey;
    drawn: FontKey;
    reason: FontSubstitutionReason;
}

const substitutionListeners = new Set<(s: FontSubstitution) => void>();

/**
 * Subscribe to font substitutions. Returns an unsubscribe function.
 *
 * A substitution is how text ends up in the wrong typeface, and the only
 * signal it ever had was a `Logger.warn` — which hosts run below the default
 * log level, so in practice it was silent. An editor can show it on the text
 * node's font field, a bake can refuse to publish on it, and a converter can
 * stop emitting a name nothing can draw.
 *
 * Fires once per distinct requested face, on the same one-shot gate as the
 * log line, so a thousand paragraphs in one family report once. The gate is
 * cleared for a family whenever one of its faces is registered, declared
 * (newly) or has its declaration withdrawn, so a family whose state changes
 * (promised, arrived, withdrawn) and then fails for a different reason is
 * reported again. A retry that fails again changes nothing, so it reports
 * nothing: that is `onHostedFontFailure`'s signal. Other families' reports
 * stand.
 */
export function onFontSubstitution(listener: (s: FontSubstitution) => void): () => void {
    substitutionListeners.add(listener);
    return () => { substitutionListeners.delete(listener); };
}

function warnOnce(requested: string, fallback: FontKey): void {
    const r = parseKey(requested);
    let seen = warnedKeys.get(r.family);
    if (!seen) warnedKeys.set(r.family, seen = new Set());
    if (seen.has(requested)) return;
    seen.add(requested);
    // A declared face is on its way; saying it "is not bundled" would report
    // the normal first paint of every hosted paragraph as a substitution, and
    // drown the warnings that are real ones.
    const loading = declaredKeys.has(requested);
    if (loading) {
        Logger.debug(
            `[Text] Font "${r.family} ${r.weight} ${r.style}" is published but not loaded yet; ` +
            `drawing "${fallback.family} ${fallback.weight} ${fallback.style}" until it arrives.`,
        );
    } else {
        Logger.warn(
            `[Text] Font "${r.family} ${r.weight} ${r.style}" is not available; ` +
            `falling back to "${fallback.family} ${fallback.weight} ${fallback.style}".`,
        );
    }
    emitSubstitution({ requested: r, drawn: fallback, reason: loading ? 'loading' : 'unavailable' });
}

function emitSubstitution(event: FontSubstitution): void {
    for (const listener of substitutionListeners) {
        try {
            listener(event);
        } catch (error) {
            // A misbehaving listener must not stop the remaining ones, and
            // must never take a paragraph down with it.
            Logger.warn(`[Text] font substitution listener threw: ${String(error)}`);
        }
    }
}
